import { Track } from 'livekit-client';
import { useEffect, useMemo, useRef } from 'react';
import Iconify from 'src/components/iconify';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';

type Props = {
  meetId?: string;
  identity?: string;
  camera?: boolean;
  isFromNav?: boolean;
};

export const ParticipantVideoStream = ({
  meetId,
  identity,
  camera,
  isFromNav,
}: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoRefDiv = useRef<HTMLDivElement>(null);
  const videoLoaded = useRef(false);

  const { myMeetId, room } = useLivekitRTCState();

  const participant = useMemo(() => {
    if (!room || !identity) return undefined;
    return meetId === myMeetId
      ? room.localParticipant
      : room.remoteParticipants.get(identity);
  }, [meetId, myMeetId, identity, room]);

  const device = useMemo(
    () => (camera ? Track.Source.Camera : Track.Source.ScreenShare),
    [camera],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const videoTrack = useMemo(() => {
    return participant?.getTrackPublication(device)?.videoTrack;
  }, [device, participant?.getTrackPublication(device)?.videoTrack]);

  useEffect(() => {
    if (videoLoaded.current || !videoTrack) return;

    videoTrack.attach(videoRef.current as HTMLVideoElement);
    videoLoaded.current = true;

    return () => {
      videoLoaded.current = false;
      videoTrack.detach(videoRef.current as HTMLVideoElement);
    };
  }, [videoTrack]);

  if (!meetId || !videoTrack) {
    if (videoLoaded.current) return null;
    return <Iconify icon="eos-icons:bubble-loading" width={0.2} />;
  }

  return (
    <div
      ref={videoRefDiv}
      id={`div_meet_video_${meetId}`}
      className="div_meet_video_stream"
      style={{
        width: '100%',
        height: '100%',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <video
        ref={videoRef}
        id={`meet_video_${meetId}`}
        autoPlay
        muted
        playsInline
        style={{
          height: '100%',
          width: '100%',
          margin: 'auto',
          borderRadius: isFromNav ? '8px 8px 0 0' : '8px',
          transform: meetId === myMeetId && camera ? 'scaleX(-1)' : 'none',
          objectFit: camera ? 'cover' : 'contain',
        }}
      />
    </div>
  );
};
